import { useState } from 'react';
import { styled } from '@mui/material';
import type { ImageProps } from 'next/image';
import { allowedImageDomains } from './allowedImageDomains';

export type AppLogoImageProps = Omit<ImageProps, 'src' | 'alt'> & {
  appName: string;
  appLogoUrl: string;
  translatedText?: {
    logoAltText?: string;
  };
  FlexibleImageComponent: React.ComponentType<ImageProps>;
};

const ImageContainerAppLogoImage = styled('div')`
  background-color: white;
  align-content: center;
  height: 100%;
  width: 100%;

  #app-logo-image-custom {
    height: auto !important;
    min-height: auto !important;
  }
`;

/**
 * Props for the AppLogoImage component from [MicroLibraryCoreUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryCoreUI)
 *
 * @param {AppLogoImageProps} props - The props for the AppLogoImage component.
 * @param {React.ComponentType<ImageProps>} FlexibleImageComponent - The image component to use.
 * @param {string} translatedText.logoAltText - The text to display in the alt attribute.
 * @example translatedText.logoAltText:
 * ```tsx
 * const logoAltText = t("appCommon:logoAltText", { appName });
 * ```
 * @returns {JSX.Element} The AppLogoImage component.
 */
export const AppLogoImage = ({
  appName,
  appLogoUrl = '#',
  fallbackLogoImageURL = '#',
  FlexibleImageComponent,
  translatedText,
  ...props
}: AppLogoImageProps & { fallbackLogoImageURL?: string }) => {
  const matchingDomain =
    process.env.NODE_ENV === 'development'
      ? allowedImageDomains.domains.find((domain) =>
          appLogoUrl?.includes(domain)
        )
      : true;

  const [logo, setLogo] = useState(appLogoUrl);

  const handleError = () => {
    console.error('Logo failed to load, switching to fallback URL');
    setLogo(fallbackLogoImageURL);
  };

  return (
    <ImageContainerAppLogoImage>
      <FlexibleImageComponent
        id="app-logo-image-custom"
        src={matchingDomain ? logo : fallbackLogoImageURL}
        alt={translatedText?.logoAltText ?? appName}
        onError={handleError}
        {...props}
      />
    </ImageContainerAppLogoImage>
  );
};
